import { useMyPermissions } from '../../hooks/useMyPermissions';
import { useTranslation } from 'react-i18next';
import React from 'react';
import { Drawer, List, ListItem, ListItemIcon, Box, ListItemButton, Tooltip, Typography, Collapse, Chip } from '@mui/material';
import {
  DashboardOutlined,
  DescriptionOutlined,
  BusinessOutlined,
  LocationOnOutlined,
  GroupsOutlined,
  SettingsOutlined,
  PaymentOutlined,
  SupportAgentOutlined,
  CheckCircleOutlined,
  ExpandLess,
  ExpandMore,
  ArrowRight,
  WorkOutlineOutlined,
  AssignmentTurnedInOutlined,
  AdminPanelSettingsOutlined,
  BarChartOutlined,
  InboxOutlined,
  PolicyOutlined,
  DesignServicesOutlined,

  EventNoteOutlined,
  TimelapseOutlined,
  BeachAccessOutlined,
  SecurityOutlined,
  StorageOutlined,
  HubOutlined,
  CalculateOutlined,
  GpsFixedOutlined,
  TabletMacOutlined,
  CampaignOutlined,
  AccountTreeRounded,
  AccessTimeOutlined,
  ApiOutlined,
  WebhookOutlined,
  LinkOutlined,
  Diversity3Outlined,
  PhoneIphoneOutlined,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { attendanceApi } from '../../api';
import { hasProduct } from '../../auth/productAccess';
import { RAIL, RAIL_ICON_INSET } from '../../theme/shellMetrics';
import { SIDE_MENU_SECTIONS } from './sideMenuSections';

// Common font family style to be used throughout the component
const fontFamilyStyle = {
  fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
  fontWeight: 500  // Increased font weight from default 400 to 500
};

interface SideMenuProps {
  collapsed: boolean;
  activeSection: string;
  /**
   * The org's `licensed_products`, verbatim from the server. Paired with
   * `productsResolved` because an empty array is a real answer (a lapsed
   * subscription) and must not be confused with "not fetched yet".
   */
  licensedProducts?: string[];
  productsResolved?: boolean;
}

// Define menu item interface
interface MenuItem {
  name: string;
  icon: React.ReactNode;
  path?: string;
  children?: Array<MenuItem>;
  /** Optional pending-count badge (e.g. face reviews awaiting the admin). */
  badge?: number;
  /**
   * Permission codes that make this entry USEFUL. Hidden when the caller holds none of
   * them; omitted means always shown.
   *
   * Why: this menu filtered by LICENSE and not by permission, so a role with the product
   * licensed and no permissions on it saw the whole module and got a 403 on every entry —
   * destinations that resolve to a refusal (svdy-ui §12.10). Measured 2026-08-27:
   * `hr_manager` holds 160 permissions and ZERO on `processes.*`, and it is admin-tier, so
   * that user reached the console and could open none of the eight Processes entries.
   *
   * ANY, not ALL: an entry is worth showing if the reader can do anything on it. A page
   * that lists and edits is still useful to somebody who can only list.
   *
   * UX only. The backend gates every endpoint; this decides what is worth showing.
   */
  permission?: string[];
}


const SideMenu: React.FC<SideMenuProps> = ({
 collapsed, activeSection, licensedProducts, productsResolved = false }) => {
  const { t } = useTranslation();

  /**
   * QuTime face capture is licensed, so the two face entries — and the two
   * requests that feed their badges — exist only for a tenant that EXPLICITLY
   * holds `qutime`. Fail-closed: unknown, empty and a failed licence fetch all
   * read as no.
   *
   * The requests matter as much as the entries. A PureOA-only tenant was firing
   * `/v1/attendance/approvals?type=face_enrollment` and `…clock_in_face_review`
   * on every admin page load — two guaranteed 403s per navigation, swallowed by
   * the `.catch` below, i.e. invisible noise against a face queue the org does
   * not have and must not be shown.
   */
  const faceEnabled = hasProduct(
    { products: licensedProducts ?? [], resolved: productsResolved },
    'qutime',
  );

  // Pending clock-in face reviews → badge on the Face Reviews nav item, so an
  // admin sees the reminder without opening the page (owner 2026-06-17: wanted
  // a visible reminder, not just an entry). Best-effort: a user without
  // attendance.approvals.read gets 403 → count stays 0 (badge hidden).
  const [faceReviewPending, setFaceReviewPending] = React.useState(0);
  /**
   * Pending face ENROLMENTS → badge on Face Templates.
   *
   * The same reminder the line above already gives clock-in reviews, on the type it was
   * missing — and the one where the stake is higher: `attendance.service` refuses a punch
   * while `face_template_status != 'approved'`, so an unapproved enrolment means that
   * person CANNOT CLOCK IN, and QuTime has no time clock to fall back on. Enrolment is
   * also the first step after an invitation, so a new 30-person shop starts with 30
   * people who cannot work until an admin approves them.
   *
   * A badge rather than the notification that already fires on upload, because a
   * notification is a one-off event: it is read once and gone, and eight of them are
   * eight history rows. This has to stay visible until the queue is empty — the bell is
   * the front gate, this is the backstop (session-discipline §12).
   *
   * A badge rather than a dashboard widget, because a widget is seen only on the
   * dashboard and can be removed from it, while this sits on every page. And rather than
   * an in-page filter, because a filter only helps somebody who already went looking.
   *
   * Best-effort, exactly like the line above: a user without `attendance.approvals.read`
   * gets a 403 and the count stays 0, which hides the badge rather than showing an error
   * in the navigation.
   */
  const [faceEnrolPending, setFaceEnrolPending] = React.useState(0);
  React.useEffect(() => {
    // No licence, no request. The early return is the whole point: without it a
    // PureOA-only tenant pays two 403s per page load for badges that are never
    // rendered. `faceEnabled` is in the dep array so the pair fires exactly once,
    // when the licence resolves to qutime.
    if (!faceEnabled) {
      setFaceReviewPending(0);
      setFaceEnrolPending(0);
      return;
    }
    let alive = true;
    attendanceApi.approvalsApi
      .list({ type: 'clock_in_face_review', status: 'pending' })
      .then((rows) => { if (alive) setFaceReviewPending(rows.length); })
      .catch(() => { /* no permission / offline — leave at 0 */ });
    attendanceApi.approvalsApi
      .list({ type: 'face_enrollment', status: 'pending' })
      .then((rows) => { if (alive) setFaceEnrolPending(rows.length); })
      .catch(() => { /* no permission / offline — leave at 0 */ });
    return () => { alive = false; };
  }, [faceEnabled]);
  // Define menu config for all sections
  const menuConfig: Record<string, Array<MenuItem>> = {
    Organization: [
      { name: t('common:nav.orgProfile'), icon: <BusinessOutlined fontSize="small" />, path: '/organization/profile' },
      { name: t('common:nav.branchesLocations'), icon: <LocationOnOutlined fontSize="small" />, path: '/organization/branches-locations' },
      { name: t('common:nav.departments'), icon: <HubOutlined fontSize="small" />, path: '/organization/departments' },
      { name: t('common:nav.positions'), icon: <WorkOutlineOutlined fontSize="small" />, path: '/organization/positions' },
      {
        name: t('common:nav.workers'), 
        icon: <GroupsOutlined fontSize="small" />, 
        children: [
          { name: t('common:nav.employmentTypes'), icon: <ArrowRight fontSize="small" />, path: '/organization/employment-types' },
          { name: t('common:nav.documents'), icon: <ArrowRight fontSize="small" />, path: '/organization/workers/documents' },
          { name: t('common:nav.active'), icon: <ArrowRight fontSize="small" />, path: '/organization/workers/active' },
          { name: t('common:nav.former'), icon: <ArrowRight fontSize="small" />, path: '/organization/workers/former' },
          // QuTime-only. Face enrolment is the admin side of a face-verified punch;
          // a PureOA-only org captures no biometrics, so the entry is absent
          // rather than present-and-403.
          ...(faceEnabled ? [
            { name: t('common:nav.faceTemplates'), icon: <ArrowRight fontSize="small" />, path: '/organization/workers/face-templates', badge: faceEnrolPending || undefined }
          ] : [])
        ]
      },
      { name: t('common:nav.workerGroups'), icon: <Diversity3Outlined fontSize="small" />, path: '/organization/worker-groups' },
      { name: t('common:nav.orgChart'), icon: <AccountTreeRounded fontSize="small" />, path: '/organization/chart' },
      { name: t('common:nav.events'), icon: <EventNoteOutlined fontSize="small" />, path: '/organization/events' },
      { name: t('common:nav.announcements'), icon: <CampaignOutlined fontSize="small" />, path: '/organization/announcements' }
  
    ],
    // EIGHT entries. Restored 2026-08-27 after I cut them to four the day before and the
    // owner rejected it: "这个是超级管理员的web界面".
    //
    // THE MISTAKE, WRITTEN DOWN SO IT IS NOT REPEATED. I applied `svdy-rules` §16's
    // small-and-precise reduction to the SUPER-ADMIN CONSOLE. That principle governs the
    // worker surfaces and the marketing site, where the reader is a shop owner on a phone
    // with one job to do. This module's reader is an administrator configuring a workflow
    // engine, and for them a missing entry point is not simplicity, it is a page they have
    // to remember a URL for. §14's rule about audience applies to information architecture
    // too, and I got the audience wrong.
    //
    // Nothing was ever deleted — routes, pages and permissions were untouched throughout —
    // but "reachable from a card on the Overview page" is not the same as "in the nav", and
    // treating it as equivalent is what made this look like a deletion.
    //
    // ORDER: watch → SET UP (in dependency order) → act → measure.
    //
    // REVISED 2026-09-01, and the revision reverses the 08-27 note below it. That note moved
    // configuration LAST because roles and form-components sat above the approvals an admin opens
    // daily. Right about the symptom, wrong about the fix, for three measured reasons:
    //
    //  1. CROSS-MODULE CONSISTENCY. Attendance is `watch → set up (6 groups) → act → measure` and
    //     Organization is `set up → watch → act`. Processes was the only module with configuration
    //     LAST, so the 08-27 change fixed one module by making it disagree with the other two.
    //     A user who learns "configuration is near the top" in Attendance does not re-learn it here.
    //
    //  2. THE ENGINE REFUSES THE OTHER ORDER. `resolvers.py` raises `process_role_empty` — "No
    //     active members are assigned to role 'X'. Open Processes → Roles…" — so publishing a
    //     workflow whose approval step routes to an unstaffed role FAILS. Roles are not optional
    //     and not after; they are a precondition. Measured on the owner's own tenant: 8 seeded
    //     roles, 2 staffed, 6 empty. Designer-first sends a first-timer into that refusal.
    //     (`scripts/seed_onboarding_catalog.py` still says `roles (optional)`; that is a defect in
    //     the guide, tracked separately, not a reason to order the nav by it.)
    //
    //  3. DEPENDENCY, the same principle Organization uses (branches → departments → positions →
    //     workers): approver roles and form components are the INGREDIENTS a workflow is assembled
    //     from, so they precede the Designer.
    //
    // Configuration is NOT nested under a collapsible parent. That was considered and rejected: the
    // three config pages are independent surfaces, not variants of one concept (which is what
    // Attendance's `children` groups legitimately are), and hiding them costs a click each — the
    // exact thing the note below warns about.
    //
    // Analytics stays a top-level entry rather than a view inside the request list, because
    // Attendance keeps `Calculation` and `Reports` top-level for the same reason: it answers a
    // different question (which NODE is stuck) than the list (which REQUEST is where), and
    // `ProcessOverview`'s SLA card already deep-links to it.
    //
    // See docs/adr/ADR-046-admin-side-menu-information-architecture.md — this order is now a
    // recorded decision, not a preference. Change it there first.
    //
    // KEPT from the 08-26 audit, because those were real defects rather than reductions:
    //   * `待我审批` instead of `审批` — the page is `assigned_to=me`, and a module-level
    //     label reading `审批` on an admin console promises all of them (Zoho names its
    //     equivalent "My Approvals" for the same reason);
    //   * `全部申请` present AT ALL — the org-wide list was routed with no nav entry, so
    //     "what is stuck in the organization?" had nowhere to start.
    Processes: [
      // --- Watch -------------------------------------------------------------------
      // The Overview reads definitions, instances and requests; `definitions.read` is the
      // one every widget on it needs.
      { name: t('common:nav.overview'), icon: <DashboardOutlined fontSize="small" />, path: '/processes/overview', permission: ['processes.definitions.read'] },

      // --- Set up (in dependency order: who approves → what the form holds → the flow) ----
      // `nav.processRoles`, NOT `nav.roles`: that key is also the System module's RBAC entry, so
      // both nav entries read "Roles & Permissions" for two unrelated pages — system permissions
      // vs who can be assigned an approval task. This page's own title has always been
      // "Process Roles", so the nav label was also disagreeing with the page it opens.
      { name: t('common:nav.processRoles'), icon: <AdminPanelSettingsOutlined fontSize="small" />, path: '/processes/roles', permission: ['processes.definitions.read'] },
      { name: t('common:nav.formComponents'), icon: <DescriptionOutlined fontSize="small" />, path: '/processes/form-components', permission: ['processes.definitions.read'] },
      {
        name: t('common:nav.processDesigner'),
        icon: <DesignServicesOutlined fontSize="small" />,
        path: '/processes/designer',
        // The designer READS to open a flow and MANAGES to publish one. Either makes the
        // entry worth showing; the page's own controls gate the rest.
        permission: ['processes.definitions.read', 'processes.definitions.manage'],
      },

      // --- Act ---------------------------------------------------------------------
      { name: t('common:nav.myApprovals'), icon: <AssignmentTurnedInOutlined fontSize="small" />, path: '/processes/requests/pending', permission: ['processes.requests.read'] },
      // ONE org-wide list since 2026-09-01 (ADR-046). `Request tracking` was a second entry on
      // the same rows — `sync_request_from_instance` mirrors instance → request 1:1 — and the
      // page that kept this label hid `withdrawn` and `failed`. Reads BOTH permissions because
      // the surviving list renders engine columns (current step, assignee) and offers
      // cancel/retry.
      { name: t('common:nav.allProcessRequests'), icon: <InboxOutlined fontSize="small" />, path: '/processes/requests/history', permission: ['processes.requests.read', 'processes.instances.read'] },

      // --- Measure -----------------------------------------------------------------
      // `GET /processes/analytics` is gated on `definitions.read`, not on an analytics
      // permission — there is none. Taken from the endpoint, not from the page's name.
      { name: t('common:nav.processAnalytics'), icon: <BarChartOutlined fontSize="small" />, path: '/processes/analytics', permission: ['processes.definitions.read'] },
    ],
    Attendance: [
      { name: t('common:nav.overview'), icon: <DashboardOutlined fontSize="small" />, path: '/attendance/overview' },
      // Rules sits directly under Overview because it is where attendance configuration STARTS:
      // the organization's default working week is set here, and a new tenant's path is
      // rules -> shifts -> schedules.
      //
      // Work Calendar used to hold this slot, from when it was the designated writer for the
      // work week. It is a read model now, and second-from-top promised an authority it does not
      // have — the owner clicked its Edit button expecting to edit. It moved into Schedules,
      // beside Schedule view: both are read-only calendar views of when work happens, one at
      // organization level and one per worker, and co-locating them is what makes the difference
      // legible.
      {
        name: t('common:nav.rules'),
        icon: <PolicyOutlined fontSize="small" />,
        children: [
          { name: t('common:nav.globalRules'), icon: <ArrowRight fontSize="small" />, path: '/attendance/rules/global' },
          { name: t('common:nav.deptRules'), icon: <ArrowRight fontSize="small" />, path: '/attendance/rules/department' },
          { name: t('common:nav.groupRules'), icon: <ArrowRight fontSize="small" />, path: '/attendance/rules/group' }
        ]
      },
      {
        name: t('common:nav.shifts'),
        icon: <TimelapseOutlined fontSize="small" />,
        children: [
          { name: t('common:nav.breaks'), icon: <ArrowRight fontSize="small" />, path: '/attendance/shifts/breaks' },
          { name: t('common:nav.timetables'), icon: <ArrowRight fontSize="small" />, path: '/attendance/shifts/timetables' },
          { name: t('common:nav.shiftCycles'), icon: <ArrowRight fontSize="small" />, path: '/attendance/shifts/cycles' }
        ]
      },
      {
        name: t('common:nav.schedules'),
        icon: <EventNoteOutlined fontSize="small" />,
        children: [
          // Work calendar FIRST, then the per-worker grid, then the editors.
          //
          // Both of this sidebar's own ordering principles put the organization's calendar
          // ahead of the per-worker grid: scope broad→narrow (rules: global→dept→group,
          // geofences: dept→group→worker), and derived-result-last (leaveManagement ends in
          // leaveBalances, processInstances in instanceMonitoring) — and Schedule view is the
          // fullest derivation here, resolving cycle days, rest days, holidays, leave and the
          // three-tier precedence, where Work calendar resolves only the organization layer.
          //
          // The frequency counter-argument does not apply: a parent group only EXPANDS
          // (`hasChildren ? handleClick(...)`), so the first child is not a landing page and the
          // order costs one row of scan, not a click. This sidebar demonstrably does not order
          // by frequency anyway — leaveBalances, the most-consulted page in Leave management,
          // is last in its group.
          //
          // And since 2026-08-12 Schedule view RENDERS what Work calendar declares: a grey
          // Saturday in the grid comes from the working week or the holiday calendar. The
          // calendar is the grid's legend, so the frame reads before the detail.
          //
          // Schedule view is deliberately NOT pushed to the end of the group, which is what
          // full consistency with leaveManagement would give. That pattern suits configure-then-
          // check; the daily schedule flow is the reverse — see who is uncovered, then fix it —
          // and burying the daily entry point at position five would trade real use for symmetry.
          { name: t('common:nav.workCalendar'), icon: <ArrowRight fontSize="small" />, path: '/attendance/work-calendar' },
          { name: t('common:nav.scheduleView'), icon: <ArrowRight fontSize="small" />, path: '/attendance/schedules/view' },
          { name: t('common:nav.deptSchedules'), icon: <ArrowRight fontSize="small" />, path: '/attendance/schedules/department' },
          { name: t('common:nav.workerSchedules'), icon: <ArrowRight fontSize="small" />, path: '/attendance/schedules/worker' },
          { name: t('common:nav.tempSchedules'), icon: <ArrowRight fontSize="small" />, path: '/attendance/schedules/temporary' }
        ]
      },
      {
        name: t('common:nav.leaveManagement'),
        icon: <BeachAccessOutlined fontSize="small" />,
        children: [
          { name: t('common:nav.leaveTypes'), icon: <ArrowRight fontSize="small" />, path: '/attendance/leave/types' },
          { name: t('common:nav.leavePolicies'), icon: <ArrowRight fontSize="small" />, path: '/attendance/leave/policies' },
          { name: t('common:nav.publicHolidays'), icon: <ArrowRight fontSize="small" />, path: '/attendance/leave/public-holidays' },
          { name: t('common:nav.leaveBalances'), icon: <ArrowRight fontSize="small" />, path: '/attendance/leave/balances' }
        ]
      },
      {
        name: t('common:nav.geofences'),
        icon: <GpsFixedOutlined fontSize="small" />,
        children: [
          { name: t('common:nav.deptGeofences'), icon: <ArrowRight fontSize="small" />, path: '/attendance/geofences/department' },
          { name: t('common:nav.groupGeofences'), icon: <ArrowRight fontSize="small" />, path: '/attendance/geofences/group' },
          { name: t('common:nav.workerRules'), icon: <ArrowRight fontSize="small" />, path: '/attendance/geofences/worker' }
        ]
      },
      { name: t('common:nav.kioskDevices'), icon: <TabletMacOutlined fontSize="small" />, path: '/attendance/kiosk' },
      { name: t('common:nav.timeEntries'), icon: <AccessTimeOutlined fontSize="small" />, path: '/attendance/time-entries' },
      {
        name: t('common:nav.approvals'),
        icon: <CheckCircleOutlined fontSize="small" />,
        children: [
          { name: t('common:nav.approvalRouting'), icon: <ArrowRight fontSize="small" />, path: '/attendance/approvals/routing' },
          { name: t('common:nav.allAttendanceRequests'), icon: <ArrowRight fontSize="small" />, path: '/attendance/approvals' },
          { name: t('common:nav.leave'), icon: <ArrowRight fontSize="small" />, path: '/attendance/approvals/leave' },
          { name: t('common:nav.overtime'), icon: <ArrowRight fontSize="small" />, path: '/attendance/approvals/overtime' },
          { name: t('common:nav.missedClockIn'), icon: <ArrowRight fontSize="small" />, path: '/attendance/approvals/missed-clock-in' },
          { name: t('common:nav.businessTrip'), icon: <ArrowRight fontSize="small" />, path: '/attendance/approvals/business-trip' },
          { name: t('common:nav.offSiteWork'), icon: <ArrowRight fontSize="small" />, path: '/attendance/approvals/off-site-work' },
          { name: t('common:nav.scheduleChange'), icon: <ArrowRight fontSize="small" />, path: '/attendance/approvals/schedule-change' },
          // Same licence as Face templates above, and the same flag drives both —
          // two flags for one capability is how the two surfaces would drift apart.
          ...(faceEnabled ? [
            { name: t('common:nav.faceReview'), icon: <ArrowRight fontSize="small" />, path: '/attendance/approvals/face-review', badge: faceReviewPending || undefined }
          ] : []),
        ]
      },
      { name: t('common:nav.attendanceCalc'), icon: <CalculateOutlined fontSize="small" />, path: '/attendance/calculation' },
      { name: t('common:nav.attendanceReports'), icon: <DescriptionOutlined fontSize="small" />, path: '/attendance/reports' }
    ],
    Integrations: [
      { name: t('common:nav.overview'), icon: <DashboardOutlined fontSize="small" />, path: '/integrations/overview' },
      { name: t('common:nav.connectors'), icon: <LinkOutlined fontSize="small" />, path: '/integrations/connectors' },
      { name: t('common:nav.apiKeys'), icon: <ApiOutlined fontSize="small" />, path: '/integrations/api' },
      { name: t('common:nav.webhooks'), icon: <WebhookOutlined fontSize="small" />, path: '/integrations/webhooks' },
    ],
    System: [
      { name: t('common:nav.overview'), icon: <DashboardOutlined fontSize="small" />, path: '/system/overview' },
      {
        name: t('common:nav.roles'),
        icon: <SecurityOutlined fontSize="small" />,
        path: '/system/access-control/roles'
      },
      {
        name: t('common:nav.auditLogs'),
        icon: <StorageOutlined fontSize="small" />,
        children: [
          { name: t('common:nav.accessLogs'), icon: <ArrowRight fontSize="small" />, path: '/system/audit-logs/access' },
          { name: t('common:nav.activityLogs'), icon: <ArrowRight fontSize="small" />, path: '/system/audit-logs/activity' }
        ]
      },
      {
        name: t('common:nav.mobileDevices'),
        icon: <PhoneIphoneOutlined fontSize="small" />,
        path: '/system/devices',
      },
      {
        name: t('common:nav.billing'),
        icon: <PaymentOutlined fontSize="small" />,
        path: '/system/billing',
      },
      {
        name: t('common:nav.support'),
        icon: <SupportAgentOutlined fontSize="small" />,
        path: '/system/support',
      },
      {
        name: t('common:nav.settings'),
        icon: <SettingsOutlined fontSize="small" />,
        children: [
          { name: t('common:nav.settingsOverview'), icon: <ArrowRight fontSize="small" />, path: '/system/settings' },
          { name: t('common:nav.workerDefaults'), icon: <ArrowRight fontSize="small" />, path: '/system/settings/workers' },
          { name: t('common:nav.security'), icon: <ArrowRight fontSize="small" />, path: '/system/settings/security' },
          { name: t('common:nav.notifications'), icon: <ArrowRight fontSize="small" />, path: '/system/settings/notifications' },
          { name: t('common:nav.dataExport'), icon: <ArrowRight fontSize="small" />, path: '/system/settings/data-export' },
          { name: t('common:nav.sso'), icon: <ArrowRight fontSize="small" />, path: '/system/settings/sso' },
        ]
      },
    ],
  };
  
  // Common styles for list item components
  const listItemButtonStyle = {
    minHeight: 48,
    px: 1.5,
    ...fontFamilyStyle,
    '&:hover': {
      backgroundColor: 'rgba(0, 0, 0, 0.04)'
    }
  };
  
  const listItemIconStyle = {
    minWidth: 0,
    mr: 2,
    justifyContent: 'center',
  };
  
  // Keeps SIDE_MENU_SECTIONS honest. If a section is added to menuConfig
  // without being added to the exported set, MainLayout would hide its rail and
  // the whole menu would silently disappear — so fail loudly in development
  // rather than shipping an invisible navigation.
  if (process.env.NODE_ENV !== 'production') {
    const configured = Object.keys(menuConfig);
    const missing = configured.filter((k) => !SIDE_MENU_SECTIONS.has(k));
    if (missing.length > 0) {
      // eslint-disable-next-line no-console
      console.error(
        `SideMenu: sections missing from SIDE_MENU_SECTIONS: ${missing.join(', ')}`,
      );
    }
  }

  // Typography style for menu items
  const typographyStyle = {
    fontSize: '14px',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    ...fontFamilyStyle,
  };

  const navigate = useNavigate();
  const { canAny } = useMyPermissions();
  const [openItems, setOpenItems] = React.useState<Record<string, boolean>>({});
  
  const handleClick = (name: string) => {
    setOpenItems(prev => ({
      ...prev,
      [name]: !prev[name]
    }));
  };
  
  const handleNavigate = (path: string) => {
    navigate(path);
  };
  
  /**
   * Drop entries the caller cannot use, and drop a PARENT once every child is gone —
   * otherwise a group whose children were all hidden stays as an expandable row that opens
   * onto nothing, which is worse than either showing or hiding it.
   *
   * `can()` fails OPEN (unresolved, or a failed fetch, shows everything), so a network blip
   * never blanks the menu for a user who does have access. See `useMyPermissions`.
   */
  const visibleItems = (items: MenuItem[]): MenuItem[] => items.reduce<MenuItem[]>(
    (kept, item) => {
      if (item.permission && !canAny(...item.permission)) return kept;
      if (item.children) {
        const children = visibleItems(item.children);
        if (children.length === 0) return kept;
        kept.push({ ...item, children });
        return kept;
      }
      kept.push(item);
      return kept;
    },
    [],
  );

  const renderMenuItems = (items: MenuItem[], level = 0) => {
    return visibleItems(items).map((item, index) => {
      const isOpen = !!openItems[item.name];
      const hasChildren = !!item.children && item.children.length > 0;
      
      return (
        <React.Fragment key={`${item.name}-${index}`}>
          <ListItem
            disablePadding
            sx={{ 
              display: 'block',
              pl: level * 2
            }}
          >
            <ListItemButton
              onClick={() => hasChildren ? handleClick(item.name) : item.path && handleNavigate(item.path)}
              sx={{
                ...listItemButtonStyle,
                justifyContent: collapsed ? 'center' : 'initial',
                // The icon's left edge is the same x in BOTH states, so collapsing the
                // rail fades the labels out without moving the icon column. Derived
                // from centring a 24px icon in the collapsed rail rather than chosen,
                // which is why it is not the content GUTTER.
                px: collapsed ? 0 : `${RAIL_ICON_INSET}px`,
              }}
            >
              {item.icon && (
                <ListItemIcon
                  sx={{
                    ...listItemIconStyle,
                    width: collapsed ? '100%' : 'auto',
                    mr: collapsed ? 0 : 2,
                  }}
                >
                  {item.icon}
                </ListItemIcon>
              )}
              {!collapsed && (
                <>
                  <Tooltip 
                    title={item.name}
                    placement="right"
                    enterDelay={500}
                    PopperProps={{
                      sx: {
                        display: (theme) => ({
                          xs: 'none',
                          sm: 'block'
                        })
                      }
                    }}
                  >
                    <Box sx={{ 
                      flex: 1,
                      minWidth: 0, // Important to ensure text truncates correctly
                    }}>
                      <Typography
                        variant="body2"
                        sx={typographyStyle}
                      >
                        {item.name}
                      </Typography>
                    </Box>
                  </Tooltip>
                  {!!item.badge && (
                    <Chip
                      label={item.badge}
                      size="small"
                      color="error"
                      sx={{ height: 18, fontSize: '0.65rem', ml: 1, '& .MuiChip-label': { px: 0.75 } }}
                    />
                  )}
                  {hasChildren && (
                    <Box sx={{ ml: 1 }}>
                      {isOpen ? <ExpandLess /> : <ExpandMore />}
                    </Box>
                  )}
                </>
              )}
            </ListItemButton>
          </ListItem>
          
          {hasChildren && !collapsed && (
            <Collapse in={isOpen} timeout="auto" unmountOnExit>
              <List component="div" disablePadding>
                {renderMenuItems(item.children || [], level + 1)}
              </List>
            </Collapse>
          )}
        </React.Fragment>
      );
    });
  };
  
  return (
    <Drawer
      variant="permanent"
      open={!collapsed}
      sx={{
        width: collapsed ? RAIL.collapsed : RAIL.expanded,
        flexShrink: 0,
        '& .MuiDrawer-paper': {
          width: collapsed ? RAIL.collapsed : RAIL.expanded,
          boxSizing: 'border-box',
          border: '1px solid',
          borderColor: 'rgba(0, 0, 0, 0.2)',
          borderTop: 0,
          borderBottom: 0,
          borderInlineStart: 0,
          backgroundColor: 'white',
          transition: theme => theme.transitions.create(['width', 'transform'], {
            easing: theme.transitions.easing.sharp,
            duration: theme.transitions.duration.enteringScreen,
          }),
          overflowX: 'hidden',
          top: '64px',
          height: 'calc(100% - 64px)',
          ...fontFamilyStyle,
        },
      }}
    >
      <List sx={{ pt: 0 }}>
        {menuConfig[activeSection] && renderMenuItems(menuConfig[activeSection])}
      </List>
    </Drawer>
  );
};

export default SideMenu;